import axios from 'axios';
import { hostUI } from '../../configuraciones';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// =========================================================================
// 1. THUNK: OBTENER REGISTROS DE GRANEL DE ALMACÉN POR ID_LOGIN
// Endpoint: GET /api/v1/bitacora-almacen/granel/:id_login
// =========================================================================
export const obtenerGranelesAlmacenPorLogin = createAsyncThunk(
    'bitacoraAlmacen/obtenerGraneles',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null;
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null;

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] };
            }

            const queryParams = isObject ? { ...paramsOrId } : {};
            delete queryParams.id_login;

            const fullUrl = hostUI + `bitacora-almacen/granel/${idLogin}`;
            const res = await axios.get(fullUrl, { params: queryParams });
            return res.data;
        } catch (error) {
            console.error('Error al obtener graneles de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 1.1 THUNK: OBTENER TODOS LOS REGISTROS DE GRANEL (SIN PAGINAR)
// =========================================================================
export const obtenerTodosGranelesAlmacenPorLogin = createAsyncThunk(
    'bitacoraAlmacen/obtenerTodosGraneles',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null;
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null;

            if (!idLogin) return [];
            const fullUrl = hostUI + `bitacora-almacen/granel/${idLogin}`;
            const res = await axios.get(fullUrl, { params: { all: true } });
            const payload = res.data;
            if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
                return payload.data;
            } else if (Array.isArray(payload)) {
                return payload;
            }
            return [];
        } catch (error) {
            console.error('Error al obtener todos los graneles de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 2. THUNK: REGISTRAR LOTE A GRANEL DIRECTO (ALMACÉN)
// Endpoint: POST /api/v1/bitacora-almacen/granel
// =========================================================================
export const registrarGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarGranel',
    async (granelData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = granelData.id_login ||
                granelData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null;

            const payload = {
                id_login: idLogin,
                ...granelData,
            };

            const fullUrl = hostUI + 'bitacora-almacen/granel';
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar granel almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 3. THUNK: ACTUALIZAR GRANEL (ALMACÉN)
// Endpoint: PUT /api/v1/bitacora-almacen/granel/:id
// =========================================================================
export const actualizarGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/actualizarGranel',
    async ({ id, ...granelData }, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-almacen/granel/${id}`;
            const res = await axios.put(fullUrl, granelData);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al actualizar granel almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 4. THUNK: ELIMINAR GRANEL (ALMACÉN)
// Endpoint: DELETE /api/v1/bitacora-almacen/granel/:id
// =========================================================================
export const eliminarGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/eliminarGranel',
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-almacen/granel/${id}`;
            const res = await axios.delete(fullUrl);
            return { id, data: res.data };
        } catch (error) {
            console.error('Error al eliminar granel almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 5. THUNK: REGISTRAR FUSIÓN / EXTRACCIÓN (ALMACÉN)
// Endpoint: POST /api/v1/bitacora-almacen/granel/fusion
// =========================================================================
export const registrarFusionGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarFusion',
    async (fusionData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = fusionData.id_login ||
                fusionData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                null;

            const payload = {
                id_login: idLogin,
                ...fusionData,
            };

            const fullUrl = hostUI + 'bitacora-almacen/granel/fusion';
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar fusión almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 6. THUNK: REGISTRAR TRASLADO ENTRE INSTALACIONES (ALMACÉN)
// Endpoint: POST /api/v1/bitacora-almacen/granel/traslado
// =========================================================================
export const registrarTrasladoGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarTraslado',
    async (trasladoData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = trasladoData.id_login ||
                trasladoData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                null;

            const payload = {
                id_login: idLogin,
                ...trasladoData,
            };

            const fullUrl = hostUI + 'bitacora-almacen/granel/traslado';
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar traslado almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 7. THUNK: REGISTRAR DILUCIÓN / AJUSTE DE GRADO (ALMACÉN)
// Endpoint: POST /api/v1/bitacora-almacen/granel/dilucion
// =========================================================================
export const registrarDilucionGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarDilucion',
    async (dilucionData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = dilucionData.id_login ||
                dilucionData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                null;

            const payload = {
                id_login: idLogin,
                ...dilucionData,
            };

            const fullUrl = hostUI + 'bitacora-almacen/granel/dilucion';
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar dilución almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 8. THUNK: REGISTRAR BAJA / MERMA (ALMACÉN)
// Endpoint: POST /api/v1/bitacora-almacen/granel/baja
// =========================================================================
export const registrarBajaGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarBaja',
    async (bajaData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = bajaData.id_login ||
                bajaData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                null;

            const payload = {
                id_login: idLogin,
                ...bajaData,
            };

            const fullUrl = hostUI + 'bitacora-almacen/granel/baja';
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar baja almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 9. THUNK: REGISTRAR MUESTREO OFICIAL (ALMACÉN)
// Endpoint: POST /api/v1/bitacora-almacen/granel/muestreo
// =========================================================================
export const registrarMuestreoGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarMuestreo',
    async (muestreoData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = muestreoData.id_login ||
                muestreoData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                null;

            const payload = {
                id_login: idLogin,
                ...muestreoData,
            };

            const fullUrl = hostUI + 'bitacora-almacen/granel/muestreo';
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar muestreo almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 10. THUNK: OBTENER KARDEX / MOVIMIENTOS
// =========================================================================
export const obtenerMovimientosGranelAlmacen = createAsyncThunk(
    'bitacoraAlmacen/obtenerMovimientos',
    async (idGranel, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-almacen/granel/${idGranel}/movimientos`;
            const res = await axios.get(fullUrl);
            return res.data?.data || res.data || [];
        } catch (error) {
            console.error('Error al obtener movimientos granel almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const obtenerTodosMovimientosAlmacen = createAsyncThunk(
    'bitacoraAlmacen/obtenerTodosMovimientos',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = paramsOrId ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                null;

            if (!idLogin) return [];
            const fullUrl = hostUI + `bitacora-almacen/granel/movimientos/${idLogin}`;
            const res = await axios.get(fullUrl);
            return res.data?.data || res.data || [];
        } catch (error) {
            console.error('Error al obtener todos los movimientos de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// SLICE & REDUCERS
// =========================================================================

export const evaluarClaseSegunProcesoAlmacen = (proceso, claseOrigen = 'Blanco Joven') => {
    if (!proceso) return claseOrigen;
    const p = String(proceso).toUpperCase();
    if (p.includes('ABOCADO')) return 'Abocado con';
    if (p.includes('DESTILADO')) return 'Destilado con';
    if (p.includes('REPROCESO') || p.includes('REACONDICIONAMIENTO')) return claseOrigen || 'Blanco Joven';
    return claseOrigen || 'Blanco Joven';
};

// =========================================================================
// 11. THUNKS: REPROCESO DE ALMACÉN
// =========================================================================
export const obtenerReprocesosAlmacenPorLogin = createAsyncThunk(
    'bitacoraAlmacen/obtenerReprocesos',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null;
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null;

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] };
            }

            const queryParams = isObject ? { ...paramsOrId } : {};
            delete queryParams.id_login;

            const fullUrl = hostUI + `bitacora-almacen/reproceso/asociado/${idLogin}`;
            const res = await axios.get(fullUrl, { params: queryParams });
            return res.data;
        } catch (error) {
            console.error('Error al obtener reprocesos de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const registrarReprocesoAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarReproceso',
    async (reprocesoData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = reprocesoData.id_login ||
                reprocesoData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                null;

            const proc = reprocesoData.procesoRealizado || reprocesoData.proceso_realizado || 'REPROCESO O REACONDICIONAMIENTO';
            const finalClase = reprocesoData.claseSalida || reprocesoData.clase_salida || evaluarClaseSegunProcesoAlmacen(proc, reprocesoData.claseOrigen);

            const payload = {
                ...reprocesoData,
                procesoRealizado: proc,
                proceso_realizado: proc,
                claseSalida: finalClase,
                clase_salida: finalClase,
                id_login: idLogin
            };

            const fullUrl = hostUI + 'bitacora-almacen/reproceso';
            const res = await axios.post(fullUrl, payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar reproceso de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const actualizarReprocesoAlmacen = createAsyncThunk(
    'bitacoraAlmacen/actualizarReproceso',
    async (params, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const reprocesoData = params?.data || params?.reprocesoData || params || {};
            const idLogin = reprocesoData.id_login ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                null;

            const targetId = params?.id || params?.id_bitacora_reproceso || reprocesoData.id || reprocesoData.id_bitacora_reproceso;
            const fullUrl = hostUI + `bitacora-almacen/reproceso/${targetId}`;
            const res = await axios.put(fullUrl, { ...reprocesoData, id_login: idLogin });
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al actualizar reproceso de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const eliminarReprocesoAlmacen = createAsyncThunk(
    'bitacoraAlmacen/eliminarReproceso',
    async (id, { rejectWithValue }) => {
        try {
            const fullUrl = hostUI + `bitacora-almacen/reproceso/${id}`;
            const res = await axios.delete(fullUrl);
            return { id, ...(res.data || {}) };
        } catch (error) {
            console.error('Error al eliminar reproceso de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// =========================================================================
// 12. THUNKS: MADURACIÓN DE ALMACÉN (SECCIÓN VII)
// =========================================================================
export const obtenerMaduracionAlmacenPorLogin = createAsyncThunk(
    'bitacoraAlmacen/obtenerMaduracion',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null;
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id || null;

            if (!idLogin) return { total: 0, data: [], maduraciones: [] };
            const queryParams = isObject ? { ...paramsOrId } : {};
            delete queryParams.id_login;

            const res = await axios.get(hostUI + `bitacora-almacen/maduracion/asociado/${idLogin}`, { params: queryParams });
            return res.data;
        } catch (error) {
            console.error('Error al obtener maduración de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const registrarMaduracionAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarMaduracion',
    async (maduracionData, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = maduracionData.id_login ||
                maduracionData.idLogin ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin || null;

            const payload = { ...maduracionData, id_login: idLogin };
            const res = await axios.post(hostUI + 'bitacora-almacen/maduracion', payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar maduración de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const actualizarMaduracionAlmacen = createAsyncThunk(
    'bitacoraAlmacen/actualizarMaduracion',
    async ({ id, maduracionData }, { rejectWithValue }) => {
        try {
            const targetId = id || maduracionData?.id_bitacora_maduracion || maduracionData?.id;
            const res = await axios.put(hostUI + `bitacora-almacen/maduracion/${targetId}`, maduracionData);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al actualizar maduración de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const eliminarMaduracionAlmacen = createAsyncThunk(
    'bitacoraAlmacen/eliminarMaduracion',
    async (id, { rejectWithValue }) => {
        try {
            const res = await axios.delete(hostUI + `bitacora-almacen/maduracion/${id}`);
            return { id, ...(res.data || {}) };
        } catch (error) {
            console.error('Error al eliminar maduración de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);


// =========================================================================
// CONDICIONES AMBIENTALES DE ALMACÉN
// =========================================================================
export const obtenerCondicionesAlmacenPorLogin = createAsyncThunk(
    'bitacoraAlmacen/obtenerCondiciones',
    async (paramsOrId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const isObject = typeof paramsOrId === 'object' && paramsOrId !== null;
            const idLogin = (isObject ? paramsOrId.id_login : paramsOrId) ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id ||
                null;

            if (!idLogin) {
                return { total: 0, page: 1, limit: 10, totalPages: 1, data: [] };
            }

            const queryParams = isObject ? { ...paramsOrId } : {};
            delete queryParams.id_login;

            const fullUrl = hostUI + `bitacora-almacen/condiciones/${idLogin}`;
            const res = await axios.get(fullUrl, { params: queryParams });
            return res.data;
        } catch (error) {
            console.error('Error al obtener condiciones ambientales de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const registrarCondicionesAlmacen = createAsyncThunk(
    'bitacoraAlmacen/registrarCondiciones',
    async (data, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const idLogin = data.id_login ||
                state?.Usuario?.data?.user?.[0]?.id ||
                state?.Usuario?.user?.[0]?.id ||
                state?.Usuario?.data?.idlogin ||
                state?.Usuario?.data?.id;
            const payload = { ...data, id_login: idLogin };
            const res = await axios.post(hostUI + 'bitacora-almacen/condiciones', payload);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al registrar condiciones de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const actualizarCondicionesAlmacen = createAsyncThunk(
    'bitacoraAlmacen/actualizarCondiciones',
    async (payloadOrArgs, { rejectWithValue }) => {
        try {
            const id = payloadOrArgs.id || payloadOrArgs.id_bitacora_condiciones;
            const data = payloadOrArgs.data || payloadOrArgs;
            const res = await axios.put(hostUI + `bitacora-almacen/condiciones/${id}`, data);
            return res.data?.data || res.data;
        } catch (error) {
            console.error('Error al actualizar condiciones de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const eliminarCondicionesAlmacen = createAsyncThunk(
    'bitacoraAlmacen/eliminarCondiciones',
    async (id, { rejectWithValue }) => {
        try {
            const res = await axios.delete(hostUI + `bitacora-almacen/condiciones/${id}`);
            return { id, ...(res.data || {}) };
        } catch (error) {
            console.error('Error al eliminar condiciones de almacén:', error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const initialState = {
    condicionesRecords: [],
    loadingCondiciones: false,
    errorCondiciones: null,
    condicionesPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    granelRecords: [],
    allGranelRecords: [],
    movimientosRecords: [],
    reprocesoRecords: [],
    loadingReproceso: false,
    errorReproceso: null,
    reprocesoPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    maduracionRecords: [],
    loadingMaduracion: false,
    errorMaduracion: null,
    maduracionPagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    pagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    },
    loading: false,
    error: null,
    successMessage: null
};

export const bitacoraAlmacenSlice = createSlice({
    name: 'bitacoraAlmacen',
    initialState,
    reducers: {
        limpiarMensajesAlmacen: (state) => {
            state.error = null;
            state.successMessage = null;
        }
    },
    extraReducers: (builder) => {
        // Obtener Graneles
        builder.addCase(obtenerGranelesAlmacenPorLogin.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(obtenerGranelesAlmacenPorLogin.fulfilled, (state, action) => {
            state.loading = false;
            const payload = action.payload;
            if (payload && Array.isArray(payload.data)) {
                state.granelRecords = payload.data;
                state.pagination = {
                    total: payload.total || payload.data.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                };
            } else if (Array.isArray(payload)) {
                state.granelRecords = payload;
                state.pagination = { total: payload.length, page: 1, limit: payload.length, totalPages: 1 };
            } else {
                state.granelRecords = [];
            }
        });
        builder.addCase(obtenerGranelesAlmacenPorLogin.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Error al obtener registros de granel de almacén';
        });

        // Obtener Todos Graneles
        builder.addCase(obtenerTodosGranelesAlmacenPorLogin.fulfilled, (state, action) => {
            state.allGranelRecords = Array.isArray(action.payload) ? action.payload : [];
        });

        // Registrar Granel
        builder.addCase(registrarGranelAlmacen.fulfilled, (state, action) => {
            const nuevo = action.payload;
            if (nuevo) {
                state.granelRecords = [nuevo, ...state.granelRecords];
                state.allGranelRecords = [nuevo, ...state.allGranelRecords];
            }
            state.successMessage = "Lote a granel de almacén registrado exitosamente";
        });

        // Actualizar Granel
        builder.addCase(actualizarGranelAlmacen.fulfilled, (state, action) => {
            const updated = action.payload;
            if (updated) {
                state.granelRecords = state.granelRecords.map(g => (g.id === updated.id || g.id_bitacora_granel === updated.id_bitacora_granel) ? updated : g);
                state.allGranelRecords = state.allGranelRecords.map(g => (g.id === updated.id || g.id_bitacora_granel === updated.id_bitacora_granel) ? updated : g);
            }
            state.successMessage = "Lote a granel actualizado exitosamente";
        });

        // Eliminar Granel
        builder.addCase(eliminarGranelAlmacen.fulfilled, (state, action) => {
            const idEliminado = action.payload.id;
            state.granelRecords = state.granelRecords.filter(g => g.id !== idEliminado && g.id_bitacora_granel !== idEliminado);
            state.allGranelRecords = state.allGranelRecords.filter(g => g.id !== idEliminado && g.id_bitacora_granel !== idEliminado);
            state.successMessage = "Lote eliminado correctamente";
        });

        // Fusión
        builder.addCase(registrarFusionGranelAlmacen.fulfilled, (state, action) => {
            const nuevo = action.payload;
            if (nuevo) {
                state.granelRecords = [nuevo, ...state.granelRecords];
                state.allGranelRecords = [nuevo, ...state.allGranelRecords];
            }
            state.successMessage = "Fusión / Extracción procesada correctamente";
        });

        // Dilución
        builder.addCase(registrarDilucionGranelAlmacen.fulfilled, (state, action) => {
            const updated = action.payload;
            if (updated) {
                state.granelRecords = state.granelRecords.map(g => (g.id === updated.id || g.id_bitacora_granel === updated.id_bitacora_granel) ? updated : g);
                state.allGranelRecords = state.allGranelRecords.map(g => (g.id === updated.id || g.id_bitacora_granel === updated.id_bitacora_granel) ? updated : g);
            }
            state.successMessage = "Ajuste / Dilución aplicada correctamente";
        });

        // Baja
        builder.addCase(registrarBajaGranelAlmacen.fulfilled, (state, action) => {
            const updated = action.payload;
            if (updated) {
                state.granelRecords = state.granelRecords.map(g => (g.id === updated.id || g.id_bitacora_granel === updated.id_bitacora_granel) ? updated : g);
                state.allGranelRecords = state.allGranelRecords.map(g => (g.id === updated.id || g.id_bitacora_granel === updated.id_bitacora_granel) ? updated : g);
            }
            state.successMessage = "Baja / Merma procesada correctamente";
        });

        // Muestreo
        builder.addCase(registrarMuestreoGranelAlmacen.fulfilled, (state, action) => {
            const updated = action.payload;
            if (updated) {
                state.granelRecords = state.granelRecords.map(g => (g.id === updated.id || g.id_bitacora_granel === updated.id_bitacora_granel) ? updated : g);
                state.allGranelRecords = state.allGranelRecords.map(g => (g.id === updated.id || g.id_bitacora_granel === updated.id_bitacora_granel) ? updated : g);
            }
            state.successMessage = "Solicitud de muestreo enviada correctamente";
        });

        // Traslado
        builder.addCase(registrarTrasladoGranelAlmacen.fulfilled, (state, action) => {
            const nuevo = action.payload;
            if (nuevo) {
                state.granelRecords = [nuevo, ...state.granelRecords];
                state.allGranelRecords = [nuevo, ...state.allGranelRecords];
            }
            state.successMessage = "Traslado a instalación procesado correctamente";
        });

        // Movimientos
        builder.addCase(obtenerTodosMovimientosAlmacen.fulfilled, (state, action) => {
            state.movimientosRecords = Array.isArray(action.payload) ? action.payload : [];
        });

        // Reprocesos Almacén
        builder.addCase(obtenerReprocesosAlmacenPorLogin.pending, (state) => {
            state.loadingReproceso = true;
            state.errorReproceso = null;
        });
        builder.addCase(obtenerReprocesosAlmacenPorLogin.fulfilled, (state, action) => {
            state.loadingReproceso = false;
            const payload = action.payload;
            if (payload && Array.isArray(payload.data)) {
                state.reprocesoRecords = payload.data;
                state.reprocesoPagination = {
                    total: payload.total || payload.data.length,
                    page: payload.page || 1,
                    limit: payload.limit || 10,
                    totalPages: payload.totalPages || 1
                };
            } else if (Array.isArray(payload)) {
                state.reprocesoRecords = payload;
                state.reprocesoPagination = { total: payload.length, page: 1, limit: payload.length, totalPages: 1 };
            } else {
                state.reprocesoRecords = [];
            }
        });
        builder.addCase(obtenerReprocesosAlmacenPorLogin.rejected, (state, action) => {
            state.loadingReproceso = false;
            state.errorReproceso = action.payload || 'Error al obtener reprocesos de almacén';
        });

        builder.addCase(registrarReprocesoAlmacen.fulfilled, (state, action) => {
            const nuevo = action.payload;
            if (nuevo) {
                state.reprocesoRecords = [nuevo, ...state.reprocesoRecords];
            }
            state.successMessage = 'Reproceso de almacén registrado exitosamente';
        });

        builder.addCase(actualizarReprocesoAlmacen.fulfilled, (state, action) => {
            const updated = action.payload;
            if (updated) {
                state.reprocesoRecords = state.reprocesoRecords.map(r => (r.id === updated.id || r.id_bitacora_reproceso === updated.id_bitacora_reproceso) ? updated : r);
            }
            state.successMessage = 'Reproceso actualizado exitosamente';
        });

        builder.addCase(eliminarReprocesoAlmacen.fulfilled, (state, action) => {
            const idEliminado = action.payload.id;
            state.reprocesoRecords = state.reprocesoRecords.filter(r => r.id !== idEliminado && r.id_bitacora_reproceso !== idEliminado);
            state.successMessage = 'Reproceso eliminado correctamente';
        });

        // Maduración Almacén (Sección VII)
        builder.addCase(obtenerMaduracionAlmacenPorLogin.pending, (state) => {
            state.loadingMaduracion = true;
            state.errorMaduracion = null;
        });
        builder.addCase(obtenerMaduracionAlmacenPorLogin.fulfilled, (state, action) => {
            state.loadingMaduracion = false;
            const payload = action.payload;
            let records = [];
            if (Array.isArray(payload?.maduraciones) && payload.maduraciones.length > 0) {
                records = payload.maduraciones;
            } else if (Array.isArray(payload?.data)) {
                records = payload.data;
            } else if (Array.isArray(payload)) {
                records = payload;
            }
            state.maduracionRecords = records;
            state.maduracionPagination = {
                total: payload?.total || records.length,
                page: payload?.page || 1,
                limit: payload?.limit || records.length || 10,
                totalPages: payload?.totalPages || 1
            };
        });
        builder.addCase(obtenerMaduracionAlmacenPorLogin.rejected, (state, action) => {
            state.loadingMaduracion = false;
            state.errorMaduracion = action.payload || action.error?.message;
        });

        builder.addCase(registrarMaduracionAlmacen.fulfilled, (state, action) => {
            if (action.payload) {
                state.maduracionRecords.unshift(action.payload);
            }
            state.successMessage = 'Registro de maduración de almacén exitoso';
        });

        builder.addCase(actualizarMaduracionAlmacen.fulfilled, (state, action) => {
            const updated = action.payload;
            if (updated) {
                const idx = state.maduracionRecords.findIndex(r =>
                    (r.id_bitacora_maduracion && r.id_bitacora_maduracion === updated.id_bitacora_maduracion) ||
                    (r.id && r.id === updated.id)
                );
                if (idx !== -1) {
                    state.maduracionRecords[idx] = { ...state.maduracionRecords[idx], ...updated };
                }
            }
            state.successMessage = 'Registro de maduración de almacén actualizado';
        });

        builder.addCase(eliminarMaduracionAlmacen.fulfilled, (state, action) => {
            const idEliminado = action.payload?.id;
            if (idEliminado) {
                state.maduracionRecords = state.maduracionRecords.filter(r =>
                    (r.id_bitacora_maduracion !== idEliminado) && (r.id !== idEliminado)
                );
            }
            state.successMessage = 'Registro de maduración eliminado con éxito';
        });


        // ==========================================
        // CONDICIONES AMBIENTALES
        // ==========================================
        builder.addCase(obtenerCondicionesAlmacenPorLogin.pending, (state) => {
            state.loadingCondiciones = true;
            state.errorCondiciones = null;
        });
        builder.addCase(obtenerCondicionesAlmacenPorLogin.fulfilled, (state, action) => {
            state.loadingCondiciones = false;
            const payload = action.payload;
            let records = [];
            if (Array.isArray(payload?.condiciones) && payload.condiciones.length > 0) {
                records = payload.condiciones;
            } else if (Array.isArray(payload?.data)) {
                records = payload.data;
            } else if (Array.isArray(payload)) {
                records = payload;
            }
            state.condicionesRecords = records;
            state.condicionesPagination = {
                total: payload?.total || records.length,
                page: payload?.page || 1,
                limit: payload?.limit || 10,
                totalPages: payload?.totalPages || 1
            };
        });
        builder.addCase(obtenerCondicionesAlmacenPorLogin.rejected, (state, action) => {
            state.loadingCondiciones = false;
            state.errorCondiciones = action.payload || action.error?.message;
        });

        builder.addCase(registrarCondicionesAlmacen.fulfilled, (state, action) => {
            if (action.payload) {
                state.condicionesRecords.unshift(action.payload);
            }
            state.successMessage = 'Registro de condiciones ambientales de almacén guardado exitosamente';
        });

        builder.addCase(actualizarCondicionesAlmacen.fulfilled, (state, action) => {
            const updated = action.payload;
            if (updated) {
                const targetId = updated.id_bitacora_condiciones || updated.id;
                const idx = state.condicionesRecords.findIndex(r =>
                    (r.id_bitacora_condiciones && r.id_bitacora_condiciones === targetId) ||
                    (r.id && r.id === targetId)
                );
                if (idx !== -1) {
                    state.condicionesRecords[idx] = { ...state.condicionesRecords[idx], ...updated };
                }
            }
            state.successMessage = 'Registro de condiciones ambientales actualizado';
        });

        builder.addCase(eliminarCondicionesAlmacen.fulfilled, (state, action) => {
            const idEliminado = action.payload?.id;
            if (idEliminado) {
                state.condicionesRecords = state.condicionesRecords.filter(r =>
                    r.id_bitacora_condiciones !== idEliminado && r.id !== idEliminado
                );
            }
            state.successMessage = 'Registro de condiciones eliminado exitosamente';
        });
    }
});

export const { limpiarMensajesAlmacen } = bitacoraAlmacenSlice.actions;
export default bitacoraAlmacenSlice.reducer;
